import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FiLink } from "react-icons/fi";
import { FaLinkedin, FaDeviantart } from "react-icons/fa";
import { CASE_STUDIES, LEADERSHIP_TEAM } from "../data/mock";
import {
  WhatWeDoSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
  IndustriesSection,
} from "../components/CommonSections";

const TeamCard = ({ member, isSecondRow }) => (
  <div className="group">
    <div className="relative bg-black border-2 border-[#B32125] overflow-hidden">
      <img
        src={member.image}
        alt={member.name}
        className={`w-full ${
          isSecondRow ? "h-[20rem]" : "h-100"
        } object-cover opacity-90 grayscale group-hover:grayscale-0  transition-all duration-500`}
      />
      <span className="absolute inset-0 bg-black opacity-10 group-hover:opacity-0 transition-opacity duration-500"></span>
      {member.linkedin && (
        <a
          href={member.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 right-3 bg-white p-2 rounded-full shadow-md 
          opacity-0 group-hover:opacity-100 transition-all duration-300 
          hover:scale-110"
        >
          <FaLinkedin size={16} color="#0A66C2" />
        </a>
      )}
    </div>

    <div className="p-6 bg-white text-center">
      <h4 className="text-xl font-semibold text-[#b32126] mb-1 uppercase">
        {member.name}
      </h4>

      <h4 className=" text-sm text-[#000000] font-semibold  uppercase">
        {member.role}
      </h4>
      <p className="text-sm text-[#000000]">{member.bio}</p>

      {/* Social Icons */}
      {/* <div className="flex justify-center items-center gap-3 mt-4">
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 opacity-90 grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500"
          >
            <FaLinkedin size={16} color="gray" />
          </a>
        )}

        {member.deviantart && (
          <a
            href={member.deviantart}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-[#05CC47] transition-colors"
          >
            <FaDeviantart size={16} color="gray" />
          </a>
        )}

        {member.chain && (
          <a
            href={member.chain}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-black transition-colors"
          >
            <FiLink size={16} color="gray" />
          </a>
        )}
      </div> */}
    </div>
  </div>
);

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const LeadershipPage = () => {
  const extendedTeam = [...LEADERSHIP_TEAM];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b32126] font-semibold">Our People</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Leadership Team
            </h1>
            <p className="text-xl text-gray-300">
              The people behind every product, partnership, and breakthrough at
              Infotech.Works.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* First Row - Always 2 */}
          {extendedTeam.length >= 2 && (
            <div className="grid md:grid-cols-2 gap-8">
              {extendedTeam.slice(0, 2).map((member) => (
                <TeamCard key={member.id} member={member} />
              ))}
            </div>
          )}

          {/* Remaining - Groups of 4 */}
          {chunkArray(extendedTeam.slice(2), 4).map((group, index) => {
            const cols =
              group.length === 3
                ? "lg:grid-cols-3"
                : group.length === 2
                  ? "lg:grid-cols-2"
                  : group.length === 1
                    ? "lg:grid-cols-1"
                    : "lg:grid-cols-4";

            return (
              <div
                key={index}
                className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-8`}
              >
                {group.map((member) => (
                  <TeamCard
                    key={member.id}
                    member={member}
                    isSecondRow={index === 0}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />

      <FAQSection />

      {/* Join Team CTA */}
      <section className="py-20 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Join Our Team?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            We're always looking for talented individuals who share our passion
            for technology and innovation.
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            View Open Positions
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LeadershipPage;
